import { useState, useEffect, useRef } from "react";
import { useLang } from "../contexts/LangContext";
import { convertNumberInCurrentLang } from "../utils/misc";
import {
  getWeightsForPortionType,
  getWeightRange,
  getImagePath,
  getFishSerialNumber,
} from "../utils/recipeImageUtils";

interface PortionSliderProps {
  onChange: (grams: number) => void;
  fishId: string;
  recipe: string;
  portionType: "whole" | "piece" | "portion";
  pieceType?: string; // Required when portionType is "piece"
  n_portions?: number;
  initialWeight?: number; // Initial weight per portion to set slider position
}

export default function PortionSlider({
  onChange,
  fishId,
  recipe,
  portionType,
  pieceType,
  n_portions = 1,
  initialWeight,
}: PortionSliderProps) {
  const { t, lang } = useLang();
  const [grams, setGrams] = useState(0);
  const [sliderValue, setSliderValue] = useState(0.5);
  const [checkpointWeights, setCheckpointWeights] = useState<number[]>([]);
  const [weightRange, setWeightRange] = useState({ min: 0, max: 0 });
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const lastTypeRef = useRef<{ portionType: string; pieceType: string | undefined }>({ portionType: '', pieceType: '' });
  const repositionPendingRef = useRef(false); // Track if we need to reposition when initialWeight updates
  const suppressOnChangeRef = useRef(true);

  // Load checkpoint weights and weight range
  useEffect(() => {
    const weights = getWeightsForPortionType(fishId, recipe, portionType, pieceType);
    const range = getWeightRange(fishId, recipe, portionType);
    
    if (weights && weights.length > 0 && range) {
      setCheckpointWeights(weights);
      setWeightRange(range);
      
      // Load image paths for all checkpoint weights
      const serialNumber = getFishSerialNumber(fishId);
      if (serialNumber) {
        const paths = weights.map((weight) =>
          getImagePath(fishId, serialNumber, recipe, portionType, weight, pieceType)
        );
        setImagePaths(paths);
      }
      
      // Detect type changes and mark that we need to reposition
      const typeChanged = lastTypeRef.current.portionType !== portionType || 
                          lastTypeRef.current.pieceType !== pieceType;
      const isFirstLoad = lastTypeRef.current.portionType === '';
      
      if (typeChanged || isFirstLoad) {
        lastTypeRef.current = { portionType, pieceType };
        repositionPendingRef.current = true; // Mark that we need to reposition when initialWeight arrives
        suppressOnChangeRef.current = true; // Prevent emitting onChange until we reposition
      }
    }
  }, [fishId, recipe, portionType, pieceType]);

  // Separate effect to reposition slider when initialWeight updates after type change
  useEffect(() => {
    if (!repositionPendingRef.current) return;
    if (initialWeight === undefined || initialWeight <= 0) return;
    // Critical: don't map initialWeight to slider position until the *real* range/checkpoints are loaded.
    // Otherwise we map against the default range and end up with an incorrect thumb position.
    if (checkpointWeights.length === 0) return;
    const denom = weightRange.max - weightRange.min;
    if (denom <= 0) return;

    const sliderPos = (initialWeight - weightRange.min) / denom;
    setSliderValue(Math.max(0, Math.min(1, sliderPos)));
    repositionPendingRef.current = false; // Clear the flag
    suppressOnChangeRef.current = false; // Allow emitting onChange on the next sliderValue effect
  }, [initialWeight, weightRange, checkpointWeights.length]);

  // Calculate weight based on slider position
  const calculateWeight = (sliderVal: number): number => {
    const { min, max } = weightRange;
    return min + (max - min) * sliderVal;
  };

  // Update grams when slider changes
  useEffect(() => {
    const weight = calculateWeight(sliderValue);
    const perPortionWeight = Math.round(weight);
    const totalWeight = perPortionWeight * n_portions;
    if (!suppressOnChangeRef.current) {
      onChange(perPortionWeight); // Pass per-portion weight, not total
    }
    setGrams(totalWeight); // Display shows total
  }, [sliderValue, n_portions, weightRange]);

  // Calculate opacity for each image layer
  const calculateOpacity = (imageIndex: number): number => {
    if (checkpointWeights.length === 0) return 0;
    if (checkpointWeights.length === 1) return 1; // Single image, always visible
    
    const currentWeight = calculateWeight(sliderValue);
    
    // For the first (bottom) image
    if (imageIndex === 0) {
      const currentCheckpoint = checkpointWeights[0];
      const nextCheckpoint = checkpointWeights[1];
      
      if (currentWeight <= nextCheckpoint) {
        return 1; // Fully visible up to next checkpoint
      } else {
        // Could fade out if needed, but for now stay at 1
        return 1;
      }
    }
    
    // For the last (top) image
    if (imageIndex === checkpointWeights.length - 1) {
      const prevCheckpoint = checkpointWeights[imageIndex - 1];
      const currentCheckpoint = checkpointWeights[imageIndex];
      
      if (currentWeight <= prevCheckpoint) {
        return 0; // Not visible below previous checkpoint
      } else if (currentWeight >= currentCheckpoint) {
        return 1; // Fully visible at or above its checkpoint
      } else {
        // Fade in between previous and current checkpoint
        return (currentWeight - prevCheckpoint) / (currentCheckpoint - prevCheckpoint);
      }
    }
    
    // For middle images (if there are more than 2)
    const prevCheckpoint = checkpointWeights[imageIndex - 1];
    const currentCheckpoint = checkpointWeights[imageIndex];
    const nextCheckpoint = checkpointWeights[imageIndex + 1];
    
    if (currentWeight <= prevCheckpoint) {
      return 0; // Not visible below previous checkpoint
    } else if (currentWeight >= currentCheckpoint && currentWeight <= nextCheckpoint) {
      return 1; // Fully visible between its checkpoint and next
    } else if (currentWeight < currentCheckpoint) {
      // Fade in between previous and current checkpoint
      return (currentWeight - prevCheckpoint) / (currentCheckpoint - prevCheckpoint);
    } else {
      // Stay visible but could fade out toward next image
      return 1;
    }
  };

  // Handle edge case of no images
  if (checkpointWeights.length === 0) {
    return (
      <div className="w-full max-w-md mx-auto px-0 text-center">
        <p className="text-gray-500">No images available</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto px-4 text-center flex flex-col items-center justify-center">
      {/* Image stack with opacity interpolation */}
      <div className="relative w-full aspect-square bg-transparent mb-4 rounded-lg overflow-hidden">
        {imagePaths.map((imgPath, index) => (
          <img
            key={index}
            src={imgPath}
            alt={`Portion ${index + 1}`}
            style={{ opacity: calculateOpacity(index) }}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          />
        ))}
      </div>

      {/* Slider with checkpoint markers */}
      <div className="relative w-full h-[24px]">
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={sliderValue}
          onChange={(e) => setSliderValue(parseFloat(e.target.value))}
          className="w-full h-4 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
        
        {/* Checkpoint markers */}
        <div className="absolute left-[20px] top-0 w-[calc(100%-40px)] h-[24px] pointer-events-none">
          {checkpointWeights.map((weight, index) => {
            const position = (weight - weightRange.min) / (weightRange.max - weightRange.min);
            const positionPercent = position * 100;
            return (
              <div
                key={index}
                className="absolute w-3 h-3 rounded-full border-2 border-white shadow-md"
                style={{
                  left: `${positionPercent}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: index === 0 ? '#10b98160' : index === checkpointWeights.length - 1 ? '#ef444460' : '#3b82f660'
                }}
                title={`${weight}g`}
              />
            );
          })}
        </div>
      </div>

      {/* Labels */}
      <div className="w-full flex justify-between text-xl text-gray-500 mt-2 select-none">
        <span>{t("portionSize_small")}</span>
        <span>{t("portionSize_medium")}</span>
        <span>{t("portionSize_large")}</span>
      </div>

      <p className="mt-4 text-sm font-semibold text-gray-700 flex flex-col items-center gap-2">
        <span>{t("estimatedAmount")}</span>
        <span className="text-2xl font-bold bg-rose-600 text-rose-100 px-4 py-1 rounded-xl">
          {convertNumberInCurrentLang(lang, grams)} {t("unit_gm")}
        </span>
      </p>
    </div>
  );
}
